import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useSessionTimer } from '../hooks/useSessionTimer';
import api from '../services/api';
import logger from '../utils/logger';
import TenantContextSwitcher from './TenantContextSwitcher';
import NotificationBell from './NotificationBell';
import { useTour } from '../contexts/TourContext';
import {
  LayoutDashboard,
  BookOpen,
  Building2,
  Radio,
  Cpu,
  Settings,
  User,
  Users,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Crown,
  Shield,
  Menu,
  X,
  LucideIcon,
  AlertTriangle,
  BarChart3,
  FileText,
  Clock,
  Brain,
  HelpCircle,
  Server
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const {
    isSuperAdminMode,
    selectedTenantSlug,
    availableTenants
  } = useTenant();
  const { startTour } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [appTitle, setAppTitle] = useState('Cold Chain Monitoring');

  // Session timeout warning (warns 5 min before access token expiry)
  useSessionTimer();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadCompanySettings = useCallback(async () => {
    try {
      // If super admin is viewing a specific tenant
      if (isSuperAdminMode && selectedTenantSlug) {
        const selectedTenant = availableTenants.find(t => t.slug === selectedTenantSlug);
        if (selectedTenant) {
          setCompanyName(selectedTenant.name);
          setAppTitle('Cold Chain Monitoring');
          return;
        }
      }

      // If user belongs to a tenant, use tenant name
      if (user?.tenant_name) {
        setCompanyName(user.tenant_name);
        setAppTitle('Cold Chain Monitoring');
        return;
      }

      // For super admin in system view, use system settings
      if (user?.role === 'super_admin') {
        setCompanyName('KELZIX System');
        setAppTitle('Multi-Tenant Admin');
        return;
      }

      // Fallback to system settings
      const settings = await api.settings.getSystemSettings();
      const companyNameSetting = settings.find((s: any) => s.key === 'company_name');
      const appTitleSetting = settings.find((s: any) => s.key === 'app_title');

      if (companyNameSetting) setCompanyName(companyNameSetting.value);
      if (appTitleSetting) setAppTitle(appTitleSetting.value);
    } catch (error) {
      logger.error('Failed to load company settings:', error);
    }
  }, [user, isSuperAdminMode, selectedTenantSlug, availableTenants]);

  useEffect(() => {
    loadCompanySettings();
  }, [loadCompanySettings]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Build nav items based on user role
  // Order: Monitoring → Infrastructure → Administration → Tools → System
  const navItems: { path: string; icon: LucideIcon; label: string; comingSoon?: boolean }[] = [
    // — Monitoring (daily use) —
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/alerts', icon: AlertTriangle, label: 'Alerts' },
    { path: '/logbook', icon: BookOpen, label: 'Logbook' },
  ];

  // Analytics: supervisor+
  if (['super_admin', 'admin', 'manager', 'supervisor'].includes(user?.role || '')) {
    navItems.push({ path: '/analytics', icon: BarChart3, label: 'Analytics' });
  }

  // — Infrastructure (tenant → room → device → hub → sensor hierarchy) —
  if (user?.role === 'super_admin') {
    navItems.push({ path: '/tenants', icon: Crown, label: 'Tenants' });
    navItems.push({ path: '/rooms', icon: Building2, label: 'Rooms' });
    navItems.push({ path: '/devices', icon: Cpu, label: 'Devices' });
    navItems.push({ path: '/hubs', icon: Server, label: 'Hubs' });
    navItems.push({ path: '/sensors', icon: Radio, label: 'Sensor Nodes' });
  }

  // — Administration —
  // User management: super_admin, admin, manager
  if (['super_admin', 'admin', 'manager'].includes(user?.role || '')) {
    navItems.push({ path: '/users', icon: Users, label: 'Users' });
    navItems.push({ path: '/audit', icon: FileText, label: 'Audit & Reports' });
  }

  // — Tools —
  if (['super_admin', 'admin'].includes(user?.role || '')) {
    navItems.push({ path: '/scheduler', icon: Clock, label: 'Scheduler' });
    navItems.push({ path: '/predictions', icon: Brain, label: 'AI Predictions', comingSoon: true });
  }

  // — System —
  navItems.push({ path: '/settings', icon: Settings, label: 'Settings' });
  navItems.push({ path: '/profile', icon: User, label: 'Profile' });

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Shared sidebar content used by both desktop and mobile
  const sidebarContent = (showLabels: boolean) => (
    <>
      <div className="p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          {showLabels && (
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-semibold text-white leading-tight truncate">
                {companyName || 'Your Company'}
              </h1>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{appTitle}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">Powered by KELZIX</p>
            </div>
          )}
          {/* Desktop: toggle collapse | Mobile: close button */}
          <button
            onClick={() => {
              if (window.innerWidth < 768) {
                setMobileMenuOpen(false);
              } else {
                setSidebarOpen(!sidebarOpen);
              }
            }}
            className="text-gray-400 hover:text-white hover:bg-gray-800 p-2 rounded-lg transition-colors flex-shrink-0"
          >
            {window.innerWidth < 768 ? (
              <X size={20} />
            ) : sidebarOpen ? (
              <ChevronLeft size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
          </button>
        </div>

        <nav className="space-y-1" data-tour="sidebar-nav">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const tourId = item.label.toLowerCase().replace(/[^a-z]/g, '-').replace(/-+/g, '-');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-tour={`${tourId}-nav`}
                className={`flex items-center px-3 py-2.5 rounded-lg transition-colors duration-150 ${
                  isActive(item.path)
                    ? 'bg-primary-600 text-white'
                    : item.comingSoon
                    ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <IconComponent size={20} className="flex-shrink-0" />
                {showLabels && (
                  <span className="ml-3 text-sm font-medium truncate flex items-center gap-2">
                    {item.label}
                    {item.comingSoon && (
                      <span className="text-[9px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
                        Soon
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <hr className="border-gray-800 my-4" />

        <button
          onClick={handleLogout}
          className="flex items-center px-3 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors duration-150 w-full"
        >
          <LogOut size={20} className="flex-shrink-0" />
          {showLabels && <span className="ml-3 text-sm font-medium">Logout</span>}
        </button>
      </div>

      {/* Spacer to push user info to bottom */}
      <div className="flex-grow"></div>

      {/* User Info - stays at bottom */}
      {user && (
        <div className={`p-3 bg-gray-800 border-t border-gray-700 flex-shrink-0 ${!showLabels && 'px-2'}`}>
          {showLabels ? (
            <>
              <p className="text-sm font-semibold truncate">{user.username}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
              <p className="text-xs text-gray-500 mt-1 capitalize">{user.role}</p>
            </>
          ) : (
            <div className="flex justify-center">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                {user.username[0].toUpperCase()}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-surface-secondary flex">

      {/* ===== MOBILE: Overlay sidebar drawer ===== */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div className="relative w-64 max-w-[80vw] bg-gray-900 text-white flex flex-col h-full z-10">
            {sidebarContent(true)}
          </div>
        </div>
      )}

      {/* ===== DESKTOP: Fixed sidebar ===== */}
      <div
        className={`hidden md:flex bg-gray-900 text-white transition-all duration-300 flex-col h-screen sticky top-0 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {sidebarContent(sidebarOpen)}
      </div>

      {/* ===== Main Content ===== */}
      <div className="flex-1 overflow-auto min-w-0">
        {/* Top Header Bar */}
        <div className={`sticky top-0 z-40 bg-white border-b border-surface-border px-3 md:px-6 py-3 ${
          user?.role === 'super_admin' && isSuperAdminMode ? 'bg-purple-50 border-purple-100' : ''
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden text-gray-600 hover:text-gray-900 p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0"
              >
                <Menu size={22} />
              </button>

              {user?.role === 'super_admin' ? (
                <>
                  <Shield className={`w-5 h-5 flex-shrink-0 ${isSuperAdminMode ? 'text-purple-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-600 truncate">
                    {isSuperAdminMode ? (
                      <span className="text-purple-700">
                        <span className="hidden sm:inline">Viewing: </span>
                        <strong className="truncate">{availableTenants.find(t => t.slug === selectedTenantSlug)?.name || selectedTenantSlug}</strong>
                      </span>
                    ) : (
                      <span><span className="hidden sm:inline">Super Admin - </span>System View</span>
                    )}
                  </span>
                </>
              ) : (
                <span className="text-sm font-medium text-gray-600 truncate">
                  <span className="hidden sm:inline">Welcome, </span>
                  <strong className="truncate">{user?.full_name || user?.username}</strong>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              {/* Help / Tour Button */}
              <button
                onClick={startTour}
                className="text-gray-400 hover:text-primary-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                title="Start guided tour"
              >
                <HelpCircle size={20} />
              </button>

              {/* Notification Bell */}
              <div data-tour="notification-bell">
                <NotificationBell />
              </div>

              {/* Tenant Switcher for Super Admin */}
              {user?.role === 'super_admin' && (
                <div data-tour="tenant-switcher">
                  <TenantContextSwitcher
                    compact
                    onTenantChange={() => {
                      window.location.reload();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-3 md:p-6 max-w-[1800px] mx-auto">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
